import React, { useEffect, useRef, useState } from "react";
import { FaPlus } from "react-icons/fa";
import { BiSolidDownArrow, BiSolidUpArrow } from "react-icons/bi";

const CalendarHeader = ({
  months,
  month,
  year,
  showMonthDropdown,
  setShowMonthDropdown,
  showYearDropdown,
  setShowYearDropdown,
  handleMonthChange,
  handleYearChange,
  setIsModalOpen,
  user,
  selectedSalon,
  setSelectedSalon,
  salones,
}) => {
  const monthDropdownRef = useRef(null);
  const yearDropdownRef = useRef(null);
  const salonDropdownRef = useRef(null);

  const [showSalonDropdown, setShowSalonDropdown] = useState(false);

  // Manejar clics fuera del menú desplegable
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        monthDropdownRef.current &&
        !monthDropdownRef.current.contains(event.target)
      ) {
        setShowMonthDropdown(false);
      }
      if (
        yearDropdownRef.current &&
        !yearDropdownRef.current.contains(event.target)
      ) {
        setShowYearDropdown(false);
      }
      if (
        salonDropdownRef.current &&
        !salonDropdownRef.current.contains(event.target)
      ) {
        setShowSalonDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [setShowMonthDropdown, setShowYearDropdown, setShowSalonDropdown]);

  return (
    <div className="flex flex-col gap-3 mb-4">
      {/* Primera fila: Calendario + Mes + Año */}
      <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
        <h2 className="text-lg sm:text-2xl font-bold text-blue-900">
          Calendario
        </h2>

        {/* Menú desplegable de meses */}
        <div className="relative" ref={monthDropdownRef}>
          <button
            onClick={() => setShowMonthDropdown(!showMonthDropdown)}
            className="bg-main-bg-sidebar text-xs sm:text-base hover:bg-blue-800 text-white px-3 sm:px-4 py-2 rounded-full flex items-center justify-between focus:outline-none min-w-[100px]"
          >
            {months[month]}{" "}
            <span className="ml-2">
              {showMonthDropdown ? (
                <BiSolidUpArrow size={16} />
              ) : (
                <BiSolidDownArrow size={16} />
              )}
            </span>
          </button>
          {showMonthDropdown && (
            <div className="absolute mt-2 bg-white border rounded-lg shadow-lg z-10 w-40 max-h-60 overflow-y-auto">
              {months.map((m, index) => (
                <div
                  key={index}
                  onClick={() => {
                    handleMonthChange(index);
                    setShowMonthDropdown(false); // Cierra el menú
                  }}
                  className="text-sm px-4 py-2 hover:bg-blue-100 cursor-pointer truncate"
                >
                  {m}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Menú desplegable de años */}
        <div className="relative" ref={yearDropdownRef}>
          <button
            onClick={() => setShowYearDropdown(!showYearDropdown)}
            className="bg-main-bg-sidebar rounded-full text-xs sm:text-base hover:bg-blue-800 text-white px-3 sm:px-4 py-2 flex items-center justify-between focus:outline-none min-w-[90px]"
          >
            {year}{" "}
            <span className="ml-2">
              {showYearDropdown ? (
                <BiSolidUpArrow size={16} />
              ) : (
                <BiSolidDownArrow size={16} />
              )}
            </span>
          </button>
          {showYearDropdown && (
            <div className="absolute mt-2 bg-white border rounded-lg shadow-lg z-10 w-32">
              {[...Array(3)].map((_, index) => (
                <div
                  key={index}
                  onClick={() => {
                    handleYearChange(2025 + index);
                    setShowYearDropdown(false); // Cierra el menú
                  }}
                  className="px-4 py-2 hover:bg-blue-100 cursor-pointer"
                >
                  {2025 + index}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Segunda fila: Salones + Nuevo */}
      <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
        {/* Menú desplegable de salones - SOLO VISIBLE PARA rol_id === 1 */}
        {user?.rol_id === 1 && (
          <div className="relative flex-1 sm:flex-initial" ref={salonDropdownRef}>
            <button
              onClick={() => setShowSalonDropdown(!showSalonDropdown)}
              className="bg-main-bg-sidebar rounded-full text-xs sm:text-base hover:bg-blue-800 text-white px-3 sm:px-4 py-2 flex items-center justify-between focus:outline-none w-full sm:w-auto min-w-[120px]"
            >
              <span className="truncate">{selectedSalon || "Salones"}</span>
              <span className="ml-2 flex-shrink-0">
                {showSalonDropdown ? <BiSolidUpArrow size={16} /> : <BiSolidDownArrow size={16} />}
              </span>
            </button>
            {showSalonDropdown && (
              <div className="absolute mt-2 bg-white border rounded-lg shadow-lg z-10 w-full sm:w-44 max-h-60 overflow-y-auto">
                <div
                  onClick={() => {
                    setSelectedSalon("");
                    setShowSalonDropdown(false);
                  }}
                  className="px-4 py-2 hover:bg-blue-100 cursor-pointer text-sm"
                >
                  Todos los Salones
                </div>
                {salones.map((salon) => (
                  <div
                    key={salon.id}
                    onClick={() => {
                      setSelectedSalon(salon.nombre);
                      setShowSalonDropdown(false);
                    }}
                    className="px-4 py-2 hover:bg-blue-100 cursor-pointer text-sm truncate"
                  >
                    {salon.nombre}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Botón "Nuevo", visible solo si el rol_id es 1 o 2 */}
        {user?.rol_id === 1 || user?.rol_id === 2 ? (
          <button
            className="flex-1 sm:flex-initial flex items-center justify-center px-4 sm:px-5 py-2 bg-main-blue text-white rounded-full hover:bg-blue-800 text-xs sm:text-base min-w-[100px]"
            onClick={() => setIsModalOpen(true)}
          >
            <FaPlus className="mr-2" size={14} />
            Nuevo
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default CalendarHeader;
